/**
 * Transforms a UTC Date object into a specific timezone.
 *
 * @param date - The UTC date to transform
 * @param timeZone - Either an offset string (e.g., '+08:00', '-05:30') or IANA timezone (e.g., 'Asia/Singapore')
 * @returns A new Date object adjusted to the specified timezone
 *
 * @example
 * const date = new Date('2023-01-01T00:00:00Z');
 * const zonedDate = utcToZonedTime(date, '+08:00');
 * console.log(zonedDate); // 2023-01-01T08:00:00Z
 */
export function utcToZonedTime(date: Date, timeZone: string): Date {
  if (!date || !(date instanceof Date)) {
    throw new Error('Invalid date provided');
  }

  // Create a new date to avoid modifying the original
  const utcDate = new Date(date.toISOString());

  // For offset format like '+08:00', '-05:30'
  if (/^[+-]\d{2}:\d{2}$/.test(timeZone)) {
    const sign = timeZone.charAt(0) === '+' ? 1 : -1;
    const hours = parseInt(timeZone.substring(1, 3), 10);
    const minutes = parseInt(timeZone.substring(4, 6), 10);

    // Calculate offset in minutes
    const offsetMinutes = sign * (hours * 60 + minutes);

    // Get the current UTC time in minutes since midnight
    const utcMinutes = utcDate.getUTCHours() * 60 + utcDate.getUTCMinutes();

    // Calculate the new local time in minutes
    let newMinutes = utcMinutes + offsetMinutes;

    // Handle day boundaries
    const dayOffset = Math.floor(newMinutes / (24 * 60));
    newMinutes = newMinutes % (24 * 60);
    if (newMinutes < 0) {
      newMinutes += 24 * 60;
    }

    // Create a new date with the adjusted time
    const result = new Date(utcDate);
    result.setUTCDate(result.getUTCDate() + dayOffset);
    result.setUTCHours(Math.floor(newMinutes / 60), newMinutes % 60);

    return result;
  }

  // For IANA timezone format (e.g., 'Asia/Singapore')
  try {
    // Using Intl to get the time difference
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    });

    const parts = formatter.formatToParts(utcDate);
    const dateObj: { [key: string]: number } = {};

    parts.forEach(part => {
      if (part.type !== 'literal') {
        dateObj[part.type] = parseInt(part.value, 10);
      }
    });

    // Create a new date with the local time parts
    const result = new Date(
      dateObj['year'],
      dateObj['month'] - 1, // JavaScript months are 0-indexed
      dateObj['day'],
      dateObj['hour'],
      dateObj['minute'],
      dateObj['second']
    );

    return result;
  } catch (e) {
    throw new Error(`Invalid timezone: ${timeZone}`);
  }
}

/**
 * Converts a date/time from a specific timezone to UTC.
 *
 * @param date - The zoned date to convert (string or Date)
 * @param timeZone - The timezone offset string (e.g., '+08:00', '-05:00')
 * @returns A new Date object representing the UTC time
 *
 * @example
 * const date = new Date('2023-01-02T00:00:00Z');
 * const utcDate = zonedTimeToUtc(date, '+08:00');
 * console.log(utcDate); // 2023-01-01T16:00:00Z
 */
export function zonedTimeToUtc(date: string | Date, timeZone: string): Date {
  if (!date) {
    throw new Error('Invalid date provided');
  }

  // Parse the input date
  const zonedDate = date instanceof Date ? date : new Date(date);

  if (isNaN(zonedDate.getTime())) {
    throw new Error('Invalid date format provided');
  }

  // Only support offset format like '+08:00', '-05:00'
  if (!/^[+-]\d{2}:\d{2}$/.test(timeZone)) {
    throw new Error('Only timezone offset format is supported (e.g., "+08:00")');
  }

  // Parse the timezone offset
  const sign = timeZone.charAt(0) === '+' ? -1 : 1; // Reverse sign for conversion to UTC
  const hours = parseInt(timeZone.substring(1, 3), 10);
  const minutes = parseInt(timeZone.substring(4, 6), 10);

  // Calculate offset in milliseconds
  const offsetMs = sign * ((hours * 60 + minutes) * 60 * 1000);

  // Create a new date by adjusting for the timezone offset
  const localTime = zonedDate.getTime();
  const utcTime = localTime + offsetMs;

  return new Date(utcTime);
}

/**
 * Interprets an ISO date string using a fixed timezone offset and returns a Date object
 * that maintains the correct time representation regardless of the local system timezone.
 *
 * @param dateString - ISO date string (e.g., "2025-03-05T17:00:00.000Z")
 * @param fixedTimeZoneOffset - The timezone offset to interpret the date in (e.g., "+08:00")
 * @returns A Date object that represents the correct time in the specified timezone
 *
 * @example
 * const result = toFixedZoneDate("2025-03-05T17:00:00.000Z", "+08:00");
 * // In a GMT+7 environment, this would show: Thu Mar 06 2025 01:00:00 GMT+0700
 */
export function toFixedZoneDate(dateString: string, fixedTimeZoneOffset: string): Date {
  if (!dateString) {
    throw new Error('Invalid date string provided');
  }

  if (!/^[+-]\d{2}:\d{2}$/.test(fixedTimeZoneOffset)) {
    throw new Error('Only timezone offset format is supported (e.g., "+08:00")');
  }

  // First, create a UTC Date from the ISO string
  const utcDate = new Date(dateString);

  if (isNaN(utcDate.getTime())) {
    throw new Error('Invalid date format provided');
  }

  // Get the offset between the fixed timezone and local system timezone
  const systemOffset = -new Date().getTimezoneOffset(); // in minutes, positive for east

  // Parse the fixed timezone offset
  const fixedSign = fixedTimeZoneOffset.charAt(0) === '+' ? 1 : -1;
  const fixedHours = parseInt(fixedTimeZoneOffset.substring(1, 3), 10);
  const fixedMinutes = parseInt(fixedTimeZoneOffset.substring(4, 6), 10);
  const fixedOffset = fixedSign * (fixedHours * 60 + fixedMinutes); // in minutes

  // Calculate the difference between the fixed timezone and system timezone
  const offsetDifference = fixedOffset - systemOffset; // in minutes

  // Apply the offset difference to the UTC date
  const offsetMs = offsetDifference * 60 * 1000;
  const adjustedTime = utcDate.getTime() + offsetMs;

  return new Date(adjustedTime);
}

/**
 * Applies a timezone offset to a UTC datetime string and returns the adjusted datetime string.
 * This function adds the timezone offset to the UTC time to get the local time in that timezone.
 *
 * @param utcDateString - UTC datetime string (e.g., "2025-06-07T10:10:11.886Z")
 * @param timezoneOffset - Timezone offset string (e.g., "+08:00", "-08:00")
 * @returns Adjusted datetime string with the offset applied
 *
 * @example
 * applyTimezoneOffset("2025-06-07T10:10:11.886Z", "+08:00"); // "2025-06-07T18:10:11.886Z"
 * applyTimezoneOffset("2025-06-07T10:10:11.886Z", "-08:00"); // "2025-06-07T02:10:11.886Z"
 */
export function applyTimezoneOffset(utcDateString: string, timezoneOffset: string): string {
  if (!utcDateString) {
    throw new Error('Invalid UTC date string provided');
  }

  if (!/^[+-]\d{2}:\d{2}$/.test(timezoneOffset)) {
    throw new Error('Only timezone offset format is supported (e.g., "+08:00")');
  }

  // Parse the UTC date string
  const utcDate = new Date(utcDateString);

  if (isNaN(utcDate.getTime())) {
    throw new Error('Invalid UTC date format provided');
  }

  // Parse the timezone offset
  const sign = timezoneOffset.charAt(0) === '+' ? 1 : -1;
  const hours = parseInt(timezoneOffset.substring(1, 3), 10);
  const minutes = parseInt(timezoneOffset.substring(4, 6), 10);

  // Calculate offset in milliseconds
  const offsetMs = sign * ((hours * 60 + minutes) * 60 * 1000);

  // Apply the offset to get the local time in the specified timezone
  const adjustedTime = utcDate.getTime() + offsetMs;
  const adjustedDate = new Date(adjustedTime);

  return adjustedDate.toISOString();
}

export function toUtcIsoString(date: Date, timeZone: string): string {
  const startOfDay = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return zonedTimeToUtc(startOfDay, timeZone).toISOString();
}

export function toDateString(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-indexed
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Extracts the date part from a datetime string and returns it in YYYY-MM-DD format.
 * This function extracts the date directly from the string without timezone conversion.
 *
 * @param dateString - Datetime string (e.g., "2025-06-07T18:17:59.342Z")
 * @returns Date string in YYYY-MM-DD format (e.g., "2025-06-07")
 *
 * @example
 * extractDateFromString("2025-06-07T18:17:59.342Z"); // "2025-06-07"
 * extractDateFromString("2025-12-25T00:00:00.000Z"); // "2025-12-25"
 */
export function extractDateFromString(dateString: string): string {
  if (!dateString) {
    throw new Error('Invalid date string provided');
  }

  // Validate that it's a valid date string by attempting to parse it
  const testDate = new Date(dateString);
  if (isNaN(testDate.getTime())) {
    throw new Error('Invalid date format provided');
  }

  // Extract date part directly from the string without timezone conversion
  const datePart = dateString.split('T')[0];

  // Validate the extracted date part format (YYYY-MM-DD)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    throw new Error('Invalid date format provided');
  }

  return datePart;
}
